import { useCallback, useEffect, useState } from "react";
import { Check, Loader } from "lucide-react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Paginator } from "primereact/paginator";
import SearchInput from "@/components/common/SearchInput";
import { Font, FontCategory } from "@/interfaces/font.interface";
import fontApi from "@/api/font";
import {
  FontActions,
  FontDetails,
  FontName,
  FontOptionId,
  FontPreview,
} from "./FontDetails";
import Uploader from "../../Uploader";
import { uploadManyFonts } from "@/utils/font";

interface Props {
  category?: FontCategory;
  onSelect?: (item: Font) => void;
}

export default function FontList({ category, onSelect }: Props) {
  const [itemList, setItemList] = useState<Font[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentItem, setCurrentItem] = useState<Font>();
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  const onPageChange = (event: any) => {
    setPage(event.page + 1);
    setSize(event.rows);
  };

  const getItemList = useCallback(() => {
    if (category) {
      setLoading(true);
      fontApi
        .getMany({
          page,
          size,
          categoryId: category._id || undefined,
          search,
          sort: "optionId",
        })
        .then((data) => {
          setItemList(data.data);
          const pagination = data.pagination;
          if (pagination) setTotalItems(pagination.totalItems);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setItemList([]);
    }
  }, [category, search, page, size]);

  useEffect(() => {
    getItemList();
  }, [getItemList]);

  const onUploadFonts = async (
    files: File[],
    onProgress: (data: string) => void,
  ) => {
    await uploadManyFonts(files, category?._id, onProgress).finally(() =>
      onFontsUploaded(),
    );
  };

  const onFontsUploaded = () => {
    if (!category) return;
    setLoading(true);
    getItemList();
  };

  const onCloseDetails = useCallback(() => {
    setCurrentItem(undefined);
  }, []);

  const onDelete = (item: Font) => {
    fontApi
      .deleteOneById(item._id)
      .then((data) => {
        const deletedItem = data.data;
        setItemList((state) => state.filter((i) => i._id !== deletedItem._id));
      })
      .catch(() => {});
  };

  return (
    <>
      <FontDetails data={currentItem} onClose={onCloseDetails} />
      <div className="flex h-full flex-col overflow-auto">
        <div className="flex items-center justify-between border-b border-gray-300 p-4 gap-2">
          <div className="flex items-center overflow-auto">
            <span className="truncate font-medium">{category?.name}</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {loading && <Loader className="animate-spin" size={20} />}
            <SearchInput
              onChange={() => setLoading(true)}
              onSearch={setSearch}
            />
            <Uploader
              accept="font"
              type="file"
              disabled={!category}
              handler={onUploadFonts}
            />
          </div>
        </div>
        <DataTable
          rowHover
          className="data-table h-full overflow-auto"
          value={itemList}
          size="small"
          scrollable
        >
          <Column
            headerClassName="w-24 max-w-24 min-w-24"
            className="w-24 max-w-24 min-w-24"
            field="optionId"
            header="Option ID"
            body={(item) => <FontOptionId data={item} />}
          ></Column>
          <Column
            headerClassName="w-40 max-w-40 min-w-40"
            className="w-40 max-w-40 min-w-40"
            field="path"
            header="Preview"
            body={(item) => <FontPreview data={item} />}
          ></Column>
          <Column
            field="name"
            header="Name"
            body={(item) => <FontName data={item} />}
          ></Column>
          <Column
            headerClassName="w-28 max-w-28 min-w-28"
            className="w-28 max-w-28 min-w-28"
            frozen
            alignFrozen="right"
            field="actions"
            header="Actions"
            body={(item) => (
              <FontActions
                data={item}
                onDelete={onDelete}
                onDetails={setCurrentItem}
              />
            )}
          ></Column>
          {onSelect && (
            <Column
              headerClassName="w-28 max-w-28 min-w-28"
              className="w-28 max-w-28 min-w-28"
              style={{ borderLeft: "1px solid #e5e7eb" }}
              frozen
              alignFrozen="right"
              body={(item) => (
                <div className="flex justify-center">
                  <Button
                    severity="info"
                    style={{
                      height: 36,
                      margin: 0,
                      padding: "6px 10px",
                      gap: 4,
                    }}
                    label="Select"
                    icon={<Check size={18} />}
                    onClick={() => onSelect(item)}
                  />
                </div>
              )}
            ></Column>
          )}
        </DataTable>
        <div className="pagination flex w-full justify-end border-t-1 border-gray-300">
          <Paginator
            first={size * (page - 1)}
            rows={size}
            totalRecords={totalItems}
            // rowsPerPageOptions={[10, 20, 50]}
            onPageChange={onPageChange}
          />
        </div>
      </div>
    </>
  );
}
